import {type CardData, EntityAction} from "@/domain/type/entity.type.ts";
import {RequestEntityState, RequestEntityStatus, type SourceDetails} from "@/domain/type/synchro.api.type.ts";
import type {RouteLocationAsRelative} from "vue-router";
import {RouteEnum} from "@/router/RouteEnum.ts";
import {useEntitiesStore} from "@/stores/entities.store.ts";
import type {ProductDetails} from "@/domain/type/product.type.ts";
import {AbstractEntity} from "@/domain/entity/AbstractEntity.ts";
import {type Source} from "@/domain/type/source.type.ts";
import {SynchroType} from "@/domain/type/synchro.type.ts";

export class Product extends AbstractEntity {
  cardIcon = 'mdi-database'
  left: ProductDetails = {
    name: '',
    code: '',
    catalog: "",
    published: false,
    theme: "",
    link: ''
  }
  right: ProductDetails = {
    name: '',
    code: '',
    catalog: "",
    published: false,
    theme: "",
    link: ''
  }

  constructor(uuid: string, leftFromApi: SourceDetails[], rightFromApi: SourceDetails[][], state: RequestEntityState, status: RequestEntityStatus) {
    super(uuid, leftFromApi, rightFromApi, state, status);
    this.left = this.hydrateDetails(leftFromApi);
    this.right = this.hydrateDetails(rightFromApi[0] ?? []);
    this.leftLink = this.left.link;
    if (this.right.link === '') {
      if (import.meta.env.VITE_AMMON_BASE_URL_OLD === import.meta.env.VITE_AMMON_BASE_URL) {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL_OLD + '/AMMON/Produits/pages/ProduitRecherche.aspx?1=1&TypeAffichage=0&ProduitsTous=true';
      } else {
        this.rightLink = import.meta.env.VITE_AMMON_BASE_URL + '/BO/s/AMMON/Produits/pages/ProduitRecherche.aspx?1=1&TypeAffichage=0&ProduitsTous=true';
      }
    } else {
      this.rightLink = this.right.link;
    }
  }

  public match(filter: string): boolean {
    return this.left.name.toLowerCase().includes(filter.toLowerCase()) ||
      this.left.code.toLowerCase().includes(filter.toLowerCase());
  }

  public getRightCardData(): CardData[] {
    return this.getCardData(this.right);
  }

  public getLeftCardData(): CardData[] {
    return this.getCardData(this.left);
  }

  fusionLink(): RouteLocationAsRelative {
    return {
      name: RouteEnum.Fusion,
      params: {
        id: this.left.code,
        sourceLeft: useEntitiesStore().sourceLeft,
        sourceRight: useEntitiesStore().sourceRight,
        type: SynchroType.PRODUCT
      }
    };
  }

  getActions(): EntityAction[] {
    return [
      EntityAction.success,
      EntityAction.fusion,
    ];
  }

  getCreateActionText(srcRight: Source): string {
    const target = srcRight.charAt(0).toUpperCase() + srcRight.slice(1).toLowerCase();
    return 'Êtes-vous certain de vouloir importer la formation dans ' + target + '?';
  }

  protected hydrateDetails(sourceDetails: SourceDetails[]) {

    const details = {
      name: '',
      code: '',
      catalog: "",
      theme: "",
      published: false,
      link: ''
    };

    sourceDetails.forEach((detail) => {
      switch (detail.label) {
        case 'nom':
          details.name = String(detail.val)
          break
        case 'reference':
          details.code = String(detail.val)
          break
        case 'Catalogues':
          if (typeof detail.val === 'string') {
            try {
              const parsed = JSON.parse(detail.val);
              if (parsed.length === 0) {
                details.catalog = 'Aucun'
              } else {
                details.catalog = String(parsed[0]?.name);
              }
            } catch {
              details.catalog = String(detail.val);
            }
          } else {
            console.error('Unexpected type for Catalogues:', typeof detail.val);
          }
          break
        case 'Thème':
          details.theme = String(detail.val)
          break
        case 'Publié dans le catalogue':
          if (detail.val === 'Non') {
            details.published = Boolean(false)
            break
          }
          details.published = Boolean(detail.val)
          break
        case 'Lien':
          details.link = this.filterLink(String(detail.val));
      }
    })

    return details
  }

  private getCardData(details: ProductDetails): CardData[] {
    return [
      {
        label: 'Nom',
        value: details.name
      },
      {
        label: 'Code',
        value: details.code
      },
      {
        label: 'Catalogues',
        value: details.catalog
      },
      {
        label: 'Thème',
        value: details.theme
      },
      {
        label: 'Publié',
        value: details.published ? 'Oui' : 'Non'
      }
    ];
  }
}
